/**
 * Estado por elemento observado, indexado por `targetKey + elementId`.
 * Nunca é module-level: cada `ViewportTracker` cria a sua própria instância.
 */
export type ViewportElementState = {
  readonly targetKey: string;
  readonly selector: string;
  readonly elementId: string;

  active: boolean;
  completed: boolean;

  candidateEnteredAt: number | undefined;
  enteredAt: number | undefined;
  exitedAt: number | undefined;

  lastRatio: number;
  maxRatio: number;

  pendingTimer: ReturnType<typeof setTimeout> | null;
};

export type ViewportStateCounts = {
  readonly active: number;
  readonly pending: number;
};

function makeStateKey(targetKey: string, elementId: string): string {
  return `${targetKey}::${elementId}`;
}

function createInitialState(
  targetKey: string,
  selector: string,
  elementId: string
): ViewportElementState {
  return {
    targetKey,
    selector,
    elementId,
    active: false,
    completed: false,
    candidateEnteredAt: undefined,
    enteredAt: undefined,
    exitedAt: undefined,
    lastRatio: 0,
    maxRatio: 0,
    pendingTimer: null,
  };
}

export class ViewportStateStore {
  private readonly states = new Map<string, ViewportElementState>();

  get(targetKey: string, elementId: string): ViewportElementState | undefined {
    return this.states.get(makeStateKey(targetKey, elementId));
  }

  set(state: ViewportElementState): void {
    this.states.set(makeStateKey(state.targetKey, state.elementId), state);
  }

  getOrCreate(
    targetKey: string,
    selector: string,
    elementId: string
  ): ViewportElementState {
    const key = makeStateKey(targetKey, elementId);
    let state = this.states.get(key);
    if (!state) {
      state = createInitialState(targetKey, selector, elementId);
      this.states.set(key, state);
    }
    return state;
  }

  delete(targetKey: string, elementId: string): void {
    const key = makeStateKey(targetKey, elementId);
    const state = this.states.get(key);
    if (state) {
      this.cancelTimer(state);
    }
    this.states.delete(key);
  }

  clear(): void {
    for (const state of this.states.values()) {
      this.cancelTimer(state);
    }
    this.states.clear();
  }

  forEach(callback: (state: ViewportElementState, key: string) => void): void {
    for (const [key, state] of this.states) {
      callback(state, key);
    }
  }

  counts(): ViewportStateCounts {
    let active = 0;
    let pending = 0;
    for (const state of this.states.values()) {
      if (state.active) active += 1;
      if (state.pendingTimer !== null) pending += 1;
    }
    return { active, pending };
  }

  cancelTimer(state: ViewportElementState): void {
    if (state.pendingTimer !== null) {
      clearTimeout(state.pendingTimer);
      state.pendingTimer = null;
    }
  }

  size(): number {
    return this.states.size;
  }
}
